import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { constant } from '@login/constants';
import { FactorService } from '@login/services/factor.service';
import { NavigationService } from '@login/services/navigation.service';
import { AdoConfig } from '@login/models/ado-config.model';
import { AdoConfigService } from '@login/services/ado-config.service';

import { AlertService } from '@shared/services/alert.service';
import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { TEXT_CONSTANTS } from '@shared/constants';
import { SpinnerService } from '@shared/services/spinner.service';

import { map, takeWhile, tap } from 'rxjs/operators';
import { timer } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import {
  DEVICE_TYPE_OPTIONS,
  OKTA_VERIFY_PUSH_TIMEOUT_MSG,
  UNEXPECTED_ERROR_MSG,
} from './mfa-setup.constants';

@Component({
  selector: 'app-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrls: ['./mfa-setup.component.scss'],
})
export class MfaSetupComponent implements OnInit {
  errorMessage: string;

  isAddingMfa = true;

  isCodeSent = false;
  isSetupStarted = false;
  isRemoveDevice = false;
  showDevices = false;
  hasActiveMfa = false;

  qrRef = '';
  qrImage: any;
  sharedSecret = '';

  time = 0;

  //Error flags
  factorExists = false;
  factorId = '';
  deviceToBeRemoved;

  activateData = {
    factorId: '',
    activateBody: {
      passCode: '',
    },
  };

  devices = [];

  // Resend Security Code Variables
  resendCodeTimer$;
  resendCodeTimeRemaining;
  showResendCodeAlert;
  showResendCodeSuccessAlert;

  currentApp: AdoConfig;

  phoneNumberForm: UntypedFormGroup;
  phoneNumberInputConfig: InputConfig;

  securityCodeForm: UntypedFormGroup;
  securityCodeInputConfig: InputConfig;

  deviceTypeForm: UntypedFormGroup;
  deviceTypeInputConfig: InputConfig;

  constructor(
    private modalService: NgbModal,
    private factorService: FactorService,
    private navigationService: NavigationService,
    private adoConfigService: AdoConfigService,
    private spinnerService: SpinnerService,
    private fb: UntypedFormBuilder,
    public alertService: AlertService,
  ) {
    this.currentApp = this.adoConfigService.getCurrentApp();
  }

  ngOnInit() {
    this.initForms();

    this.spinnerService.start();
    this.getFactors();
  }

  initForms() {
    this.initPhoneNumberForm();
    this.initSecurityCodeForm();
    this.initDeviceTypeForm();
  }

  initDeviceTypeForm() {
    this.deviceTypeForm = this.fb.group({
      type: ['', HarpValidators.required('')],
    });

    this.deviceTypeForm.get('type').valueChanges.subscribe({
      next: () => {
        this.errorMessage = '';
        this.phoneNumberForm.reset();
      },
    });

    this.deviceTypeInputConfig = {
      name: 'type',
      form: this.deviceTypeForm,
      type: 'select',
      label: 'Device Type',
      options: DEVICE_TYPE_OPTIONS,
      required: true,
    };
  }

  initSecurityCodeForm() {
    this.securityCodeForm = this.fb.group({
      securityCode: [
        '',
        [
          HarpValidators.required(
            TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('Security code'),
          ),
          HarpValidators.pattern('^[0-9]*$', 'Invalid security code'),
        ],
      ],
    });

    this.securityCodeInputConfig = {
      name: 'securityCode',
      form: this.securityCodeForm,
      label: 'Security Code',
      required: true,
      autofocus: true,
    };
  }

  initPhoneNumberForm() {
    this.phoneNumberForm = this.fb.group({
      phoneNumber: [
        '',
        [
          HarpValidators.required(
            TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('Phone Number'),
          ),
          HarpValidators.pattern(
            '^[(]{0,1}[0-9]{3}[)]{0,1}[-\\s\\.]{0,1}[0-9]{3}[-\\s\\.]{0,1}[0-9]{4}$',
            'Invalid phone number entered',
          ),
        ],
      ],
    });

    this.phoneNumberInputConfig = {
      name: 'phoneNumber',
      form: this.phoneNumberForm,
      label: 'Phone Number',
      required: true,
      autofocus: true,
    };
  }
  genericErrorHandler(error) {
    if (error.status == 0 || error.status == 500) {
      this.errorMessage = UNEXPECTED_ERROR_MSG;
    } else {
      this.errorMessage = error.message;
    }
  }

  resetResendCodeTimer() {
    this.resendCodeTimer$ = timer(30, 1000).pipe(
      map(t => 30 - t),
      takeWhile(t => t >= 0),
      tap(t => {
        if (t === 0) {
          this.showResendCodeAlert = false;
          this.showResendCodeSuccessAlert = false;
        }
        this.resendCodeTimeRemaining = t;
      }),
    );
  }

  openModal(x) {
    this.modalService.open(x);
  }

  onClickActivate() {
    this.spinnerService.start();

    this.isAddingMfa = true;
    this.deviceTypeForm.reset();

    this.activateData.activateBody.passCode = this.securityCode;
    this.activateData.factorId = this.factorId;

    this.factorService.activateFactor(this.activateData).then(
      res => {
        this.spinnerService.stop();

        if (res.errorCode == 'E0000068') {
          this.alertService.setAlert(res.errorSummary);
          this.isCodeSent = !this.isSetupStarted;
          this.securityCodeForm.reset();
          return;
        }

        if (!res.errorCode) {
          this.spinnerService.start();
          this.getFactors();
        } else {
          this.isCodeSent = false;
          this.securityCodeForm.reset();
          this.isSetupStarted = false;
        }
      },
      error => {
        this.genericErrorHandler(error);
        this.spinnerService.stop();
      },
    );
  }

  verifyOktaPushActivation() {
    this.isAddingMfa = true;
    this.isCodeSent = false;
    this.activateData.activateBody.passCode = 'polling';
    this.activateData.factorId = this.factorId;
    const TIMEOUT = 20000;

    const newtime = new Date().getTime();
    if (newtime - this.time > TIMEOUT) {
      this.onClickCancel({
        alert: { msg: OKTA_VERIFY_PUSH_TIMEOUT_MSG, type: 'danger' },
        resetDevice: false,
      });
      return;
    }

    if (this.deviceType !== 'push') {
      return;
    }

    this.factorService.activateFactor(this.activateData).then(
      res => {
        this.spinnerService.stop();

        if (res?.factorResult === 'WAITING') {
          this.verifyOktaPushActivation();
          return;
        }

        if (res?.factorResult === 'TIMEOUT') {
          this.spinnerService.start();
          this.getFactors().then(devices => {
            this.alertService.setAlert(OKTA_VERIFY_PUSH_TIMEOUT_MSG);
          });
        }

        if (
          res.status === 'ACTIVE' ||
          res.errorCode === 'FactorAlreadyExists'
        ) {
          this.getFactors().then(() => {
            this.deviceTypeForm.reset();
          });
        }
      },
      error => this.genericErrorHandler(error),
    );
  }

  onClickSendCode() {
    this.spinnerService.start();
    const payload = {
      factorType: this.deviceType === 'voice' ? 'call' : this.deviceType,
      profile: {
        phoneNumber: this.phoneNumber,
      },
      provider: 'OKTA',
    };

    this.factorExists = false;
    this.securityCodeForm.reset(); //Clear the security code, from the previous submissions

    this.factorService.createFactor(payload).then(
      res => {
        this.spinnerService.stop();

        switch (res.errorCode) {
          case 'E0000001':
            this.errorMessage =
              'Unable to process the request: ' +
              res.errorCauses[0].errorSummary;
            this.factorExists = true;
            break;
          case 'E0000133':
            this.errorMessage =
              'A phone call was just made. Please wait 30 seconds before trying again.';
            this.isCodeSent = true;
            this.showResendCodeAlert = true;
            this.showResendCodeSuccessAlert = false;
            this.securityCodeForm.reset();
            break;
          case 'E0000118':
            this.errorMessage =
              'An email was just sent. Please wait 30 seconds before trying again.';
            this.isCodeSent = true;
            this.securityCodeForm.reset();
            break;
          case 'E0000109':
            this.errorMessage =
              'An SMS message was just sent. Please wait 30 seconds before trying again.';
            this.isCodeSent = true;
            this.showResendCodeAlert = true;
            this.showResendCodeSuccessAlert = false;
            this.securityCodeForm.reset();
            break;
          default:
            if ([constant.invalidSession, constant.unkownState].includes(res)) {
              this.navigationService.goToLoginScreen();
            } else if (res.status != 'ACTIVE') {
              this.isCodeSent = true;
              if (
                this.deviceType.includes('voice') ||
                this.deviceType.includes('sms')
              ) {
                this.resetResendCodeTimer();
              }
            } else {
              this.spinnerService.start();
              this.getFactors();
            }
            break;
        }
        this.factorId = res.id;
        this.phoneNumberForm.reset();
        this.deviceTypeForm.reset();
      },
      error => {
        this.genericErrorHandler(error);
        this.isCodeSent = false;
        this.spinnerService.stop();
      },
    );
  }

  onEmailCode() {
    this.spinnerService.start();
    this.factorExists = false;
    this.securityCodeForm.reset(); //Clear the security code, from the previous submissions
    const payload = {
      factorType: 'email',
      provider: 'OKTA',
    };
    this.factorService.createFactor(payload).then(
      res => {
        if (res.errorCode == 'E0000001') {
          this.errorMessage =
            'Unable to process the request: ' + res.errorCauses[0].errorSummary;
          this.factorExists = true;
          this.spinnerService.stop();
          return;
        }
        if (res.status != 'ACTIVE') {
          this.isCodeSent = true;
          this.spinnerService.stop();
        } else {
          this.getFactors();
        }

        this.factorId = res.id;
      },
      error => {
        this.genericErrorHandler(error);
        this.isCodeSent = false;
        this.spinnerService.stop();
      },
    );
  }

  createGoogleAuth() {
    this.spinnerService.start();

    const payload = {
      factorType: 'token:software:totp',
      provider: 'GOOGLE',
    };
    this.factorService.createFactor(payload).then(
      res => {
        this.spinnerService.stop();

        if (res.errorCode == 'E0000001') {
          this.errorMessage =
            'Unable to process the request: ' + res.errorCauses[0].errorSummary;
          this.factorExists = true;
          return;
        }

        if (res.status != 'ACTIVE') {
          this.isSetupStarted = true;
        }
        this.factorId = res.id;
        this.sharedSecret = res._embedded.activation.sharedSecret;
        this.qrRef = res._embedded.activation._links.qrcode.href;
        this.factorExists = false;
        this.getImage();
      },
      error => {
        this.spinnerService.stop();

        this.genericErrorHandler(error);
        this.isSetupStarted = false;
      },
    );
  }

  createOktaVerify() {
    this.spinnerService.start();

    const payload = {
      factorType: 'token:software:totp',
      provider: 'OKTA',
    };

    this.factorService.createFactor(payload).then(
      res => {
        this.spinnerService.stop();

        if (res.errorCode == 'E0000001') {
          this.errorMessage =
            'Unable to process the request: ' + res.errorCauses[0].errorSummary;
          this.factorExists = true;
          return;
        }

        if (res.status != 'ACTIVE') {
          this.isSetupStarted = true;
        }

        this.factorId = res.id;
        this.sharedSecret = res._embedded.activation.sharedSecret;
        this.qrRef = res._embedded.activation._links.qrcode.href;
        this.factorExists = false;
        this.getImage();
      },
      error => {
        this.genericErrorHandler(error);
        this.isSetupStarted = false;
        this.spinnerService.stop();
      },
    );
  }

  createOktaVerifyPush() {
    this.spinnerService.start();

    const payload = {
      factorType: 'push',
      provider: 'OKTA',
    };

    this.factorService.createFactor(payload).then(
      res => {
        this.spinnerService.stop();

        if (res.errorCode == 'E0000001') {
          this.errorMessage =
            'Unable to process the request: ' + res.errorCauses[0].errorSummary;
          this.factorExists = true;
          return;
        }

        if (res.status != 'ACTIVE') {
          this.isSetupStarted = true;
        }

        this.factorId = res.id;
        this.sharedSecret = res._embedded.activation.sharedSecret;
        this.qrRef = res._embedded.activation._links.qrcode.href;
        this.factorExists = false;
        this.getImage();
        this.time = new Date().getTime();
        this.verifyOktaPushActivation();
      },
      error => {
        this.spinnerService.stop();

        this.genericErrorHandler(error);
        this.isSetupStarted = false;
      },
    );
  }

  getImage() {
    this.spinnerService.start();

    this.factorService.getImage(this.factorId, this.qrRef).then(
      res => {
        this.qrImage = 'data:image/jpeg;base64,' + res;
        this.spinnerService.stop();
      },
      error => {
        this.genericErrorHandler(error);
        this.isSetupStarted = false;
        this.spinnerService.stop();
      },
    );
  }

  createImageFromBlob(image: Blob) {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => (this.qrImage = reader.result),
      false,
    );

    if (image) {
      reader.readAsDataURL(image);
    }
  }

  onClickReSend() {
    this.errorMessage = null;
    this.alertService.clearAlert();

    if (this.resendCodeTimeRemaining > 0) {
      this.spinnerService.stop();
      this.showResendCodeAlert = true;
      return;
    }

    this.spinnerService.start();

    this.factorExists = false;
    this.securityCodeForm.reset(); //Clear the security code, from the previous submissions

    this.factorService.resendSecurityCode(this.factorId).then(
      res => {
        this.spinnerService.stop();
        switch (res.errorCode) {
          case 'E0000133':
            this.errorMessage =
              'A phone call was just made. Please wait 30 seconds before trying again.';
            this.isCodeSent = true;
            this.securityCodeForm.reset();
            break;
          case 'E0000118':
            this.errorMessage =
              'An email was just sent. Please wait 30 seconds before trying again.';
            this.isCodeSent = true;
            this.securityCodeForm.reset();
            break;
          case 'E0000109':
            this.errorMessage =
              'An SMS message was just sent. Please wait 30 seconds before trying again.';
            this.isCodeSent = true;
            this.securityCodeForm.reset();
            break;
          default:
            this.errorMessage = null;
            this.showResendCodeAlert = false;
            this.showResendCodeSuccessAlert = true;
            this.resetResendCodeTimer();
            break;
        }
      },
      error => {
        this.spinnerService.stop();
      },
    );
  }

  onClickSetupApp() {
    this.isSetupStarted = true;
  }

  onClickCancel(
    { alert, resetDevice } = { alert: undefined, resetDevice: true },
  ) {
    this.isCodeSent = false;
    this.isSetupStarted = false;
    this.spinnerService.start();
    this.showResendCodeAlert = false;
    this.showResendCodeSuccessAlert = false;
    if (resetDevice) {
      this.deviceTypeForm.reset();
    }

    this.getFactors().then(res => {
      if (alert) {
        this.alertService.setAlert(alert.msg, alert.type);
      }
    });
  }

  validateRemoveDevice(device) {
    this.deviceToBeRemoved = device;
    this.isAddingMfa = false;
    this.isRemoveDevice = true;
  }

  getFactors() {
    this.spinnerService.start();
    return this.factorService.getFactors().then(devices => {
      this.devices = devices;
      this.securityCodeForm.reset();
      this.setHasActiveMfa();
      this.spinnerService.stop();
      this.isCodeSent = false;
      this.isSetupStarted = false;
      this.isAddingMfa = true;

      return devices;
    });
  }

  removeDevice() {
    this.spinnerService.start();

    this.factorExists = false;

    this.factorService.deleteDevice(this.deviceToBeRemoved).then(
      () => {
        this.getFactors();
      },
      error => {
        this.genericErrorHandler(error);
        this.spinnerService.stop();
      },
    );

    this.isRemoveDevice = false;
    this.isAddingMfa = true;
    this.setHasActiveMfa();
  }

  cancelRemoveDevice() {
    this.deviceToBeRemoved = null;
    this.isRemoveDevice = false;
    this.isAddingMfa = true;
  }

  setHasActiveMfa() {
    for (let i = 0; i < this.devices.length; i++) {
      if (this.devices[i].status === 'ACTIVE') {
        this.hasActiveMfa = true;
        return;
      }
    }
    this.hasActiveMfa = false;
  }

  get deviceToBeRemovedContact() {
    const { phoneNumber, email, credentialId } =
      this.deviceToBeRemoved?.profile ?? {};
    return phoneNumber ?? email ?? credentialId;
  }

  get phoneNumber() {
    return this.phoneNumberForm.value?.phoneNumber ?? '';
  }

  get securityCode() {
    return this.securityCodeForm.value?.securityCode;
  }

  get showCompleteSetup() {
    return this.devices?.some?.(({ status }) => status === 'ACTIVE') ?? false;
  }

  private get deviceType() {
    return this.deviceTypeForm.value.type;
  }
}
